import axiosInstance from "../../components/api/axiosInstance";

const unwrap = (res) => res.data?.data;

/** Same API surface for staff (/dm, all tenants) and tenant portal (/client/dm, own tenant). */
export function createDmApi(base, { isStaff = false } = {}) {
  const get = (url, params) => axiosInstance.get(`${base}${url}`, { params }).then(unwrap);
  const post = (url, body) => axiosInstance.post(`${base}${url}`, body).then(unwrap);
  const put = (url, body) => axiosInstance.put(`${base}${url}`, body).then(unwrap);
  const del = (url) => axiosInstance.delete(`${base}${url}`).then(unwrap);

  return {
    isStaff,
    meta: () => get("/meta"),
    tenants: (search, { all = false, businessTypeId = null, includeInactive = false } = {}) =>
      get("/tenants", {
        search,
        ...(all ? { all: 1 } : {}),
        ...(businessTypeId ? { business_type_id: businessTypeId } : {}),
        ...(includeInactive ? { include_inactive: 1 } : {}),
      }),
    businessTypes: () => get("/business-types"),
    overview: (tenantId) => get("/overview", tenantId ? { tenant_id: tenantId } : undefined),

    listProjects: (params) => get("/projects", params),
    getProject: (id) => get(`/projects/${id}`),
    createProject: (body) => post("/projects", body),
    updateProject: (id, body) => put(`/projects/${id}`, body),
    deleteProject: (id) => del(`/projects/${id}`),
    summary: (id, params) => get(`/projects/${id}/summary`, params),
    projectLeads: (id, params) => get(`/projects/${id}/leads`, params),

    listContent: (projectId, params) => get(`/projects/${projectId}/content`, params),
    createContent: (projectId, body) => post(`/projects/${projectId}/content`, body),
    updateContent: (id, body) => put(`/content/${id}`, body),
    reviewContent: (id, body) => post(`/content/${id}/review`, body),
    deleteContent: (id) => del(`/content/${id}`),

    listMedia: (projectId, params) => get(`/projects/${projectId}/media`, params),
    getUploadUrl: (projectId, body) => post(`/projects/${projectId}/media/upload-url`, body),
    completeMedia: (id, body) => post(`/media/${id}/complete`, body || {}),
    updateMedia: (id, body) => put(`/media/${id}`, body),
    deleteMedia: (id) => del(`/media/${id}`),

    listPosts: (projectId, params) => get(`/projects/${projectId}/posts`, params),
    createPost: (projectId, body) => post(`/projects/${projectId}/posts`, body),
    listCalendarPosts: (params) => get("/posts", params),
    createCalendarPost: (body) => post("/posts", body),
    updatePost: (id, body) => put(`/posts/${id}`, body),
    deletePost: (id) => del(`/posts/${id}`),
    changesRecipient: (id) => get(`/posts/${id}/changes-recipient`),
    requestPostChanges: (id, body) => post(`/posts/${id}/request-changes`, body),

    listCampaigns: (projectId, params) => get(`/projects/${projectId}/campaigns`, params),
    createCampaign: (projectId, body) => post(`/projects/${projectId}/campaigns`, body),
    updateCampaign: (id, body) => put(`/campaigns/${id}`, body),
    deleteCampaign: (id) => del(`/campaigns/${id}`),
    createAdSet: (campaignId, body) => post(`/campaigns/${campaignId}/ad-sets`, body),
    updateAdSet: (id, body) => put(`/ad-sets/${id}`, body),
    deleteAdSet: (id) => del(`/ad-sets/${id}`),
    createAd: (adSetId, body) => post(`/ad-sets/${adSetId}/ads`, body),
    updateAd: (id, body) => put(`/ads/${id}`, body),
    deleteAd: (id) => del(`/ads/${id}`),

    listMetrics: (projectId, params) => get(`/projects/${projectId}/metrics`, params),
    saveMetrics: (projectId, body) => post(`/projects/${projectId}/metrics`, body),
    deleteMetric: (id) => del(`/metrics/${id}`),

    // Client setup checklist (staff pass the tenant; portal users always get their own company)
    setup: (tenantId) => get("/setup", tenantId ? { tenant_id: tenantId } : undefined),
    setupActivity: (tenantId) => get("/setup/activity", tenantId ? { tenant_id: tenantId } : undefined),
    exportSetup: (tenantId) => get("/setup/export", tenantId ? { tenant_id: tenantId } : undefined),
    getSetupStep: (id) => get(`/setup/steps/${id}`),
    updateSetupStep: (id, body) => put(`/setup/steps/${id}`, body),
    fillSetupFromSocial: (tenantId, stepId) =>
      post("/setup/social-fill", { tenant_id: tenantId || undefined, Tenant_Step_Id: stepId || undefined }),
    addSetupComment: (id, body) => post(`/setup/steps/${id}/comments`, body),
    setupUploadUrl: (id, body) => post(`/setup/steps/${id}/files/upload-url`, body),
    completeSetupFile: (id, body) => post(`/setup/steps/${id}/files/complete`, body),
    deleteSetupFile: (id, fileId) => del(`/setup/steps/${id}/files/${fileId}`),
    verifySetupStep: (id) => post(`/setup/steps/${id}/verify`, {}),
    verifyAllSetup: (tenantId) => post("/setup/verify-all", { tenant_id: tenantId }),
    remindSetup: (tenantId, body) => post("/setup/remind", { ...(body || {}), tenant_id: tenantId }),
    setupTemplate: () => get("/setup/template"),
    createSetupTemplateStep: (body) => post("/setup/template/steps", body),
    updateSetupTemplateStep: (id, body) => put(`/setup/template/steps/${id}`, body),
    createSetupCategory: (body) => post("/setup/template/categories", body),
    updateSetupCategory: (id, body) => put(`/setup/template/categories/${id}`, body),
  };
}

export const staffDmApi = createDmApi("/dm", { isStaff: true });
export const portalDmApi = createDmApi("/client/dm");

export function errorMessage(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

/** Portal users cannot delete content / media the agency team uploaded. */
export const canDelete = (api, item) => Boolean(api?.isStaff) || !item?.Agency_Upload;

export function hasService(access, codes) {
  const services = access?.services || [];
  return codes.some((c) => services.includes(c));
}

const numberFormat = new Intl.NumberFormat("en-IN");
const moneyFormat = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

export const fmtNum = (v) => (v == null || v === "" ? "—" : numberFormat.format(Number(v) || 0));
export const fmtMoney = (v) => (v == null || v === "" ? "—" : moneyFormat.format(Number(v) || 0));
export const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

/** "2026-10-06T13:08:00" -> "2026-10-06, 1:08 PM" (date only when there is no time part). */
export function fmtDateTime(v) {
  if (!v) return "—";
  const s = String(v);
  const match = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return fmtDate(v);
  const [, day, hh, mm] = match;
  const h = Number(hh);
  return `${day}, ${h % 12 || 12}:${mm} ${h < 12 ? "AM" : "PM"}`;
}

export function toISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** [{name, radius_km}] -> "LB Nagar (R2km), BN Reddy" */
export function locationsToText(list) {
  if (!Array.isArray(list)) return "";
  return list
    .map((l) => (l?.radius_km != null ? `${l.name} (R${l.radius_km}km)` : l?.name))
    .filter(Boolean)
    .join(", ");
}

export function listToText(list) {
  return Array.isArray(list) ? list.join(", ") : "";
}

const PLATFORM_LABELS = { "WhatsApp Business": "WA Business" };

/** Short display label for a stored platform value. */
export const platformLabel = (v) => PLATFORM_LABELS[v] || v;

export const nullIfBlank = (v) => (v === "" || v === undefined ? null : v);
export const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

/** Initials of a multi-word name, or the first 4 characters of a single word: "Green Valley Villas" -> "GVV". */
export function shortCodeFrom(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const code = words.length === 1 ? words[0].slice(0, 4) : words.map((w) => w[0]).join("");
  return code.replace(/[^a-z0-9]/gi, "").toUpperCase().slice(0, 10);
}

const PROJECT_CODE_MAX = 20;

/** DM project code "<company letters>-<project letters>-<client id>": Getwell Bee / Test Proj / 12 -> "GB-TP-12". */
export function projectCodeFrom({ companyName, projectName, clientId }) {
  const project = shortCodeFrom(projectName);
  if (!project) return "";
  const id = clientId ? String(clientId) : "";
  const company = shortCodeFrom(companyName);
  const room = PROJECT_CODE_MAX - id.length - project.length - (id ? 1 : 0) - 1;
  const parts = [company.slice(0, Math.max(room, 0)), project, id].filter(Boolean);
  return parts.join("-").slice(0, PROJECT_CODE_MAX);
}
